import type {
  GuestTester,
  GuestTesterInput,
  MemberCompletionState,
  TestSession,
  TestSessionDraft,
  TestSessionMember,
  UFPMScoreEntry,
} from './types'

export const SESSION_KEY = 'ufpm_test_session'

export function loadSession(storage: Storage = sessionStorage): TestSession | null {
  try {
    const raw = storage.getItem(SESSION_KEY)
    if (!raw) return null
    return JSON.parse(raw) as TestSession
  } catch {
    return null
  }
}

export function saveSession(session: TestSession, storage: Storage = sessionStorage): void {
  try {
    storage.setItem(SESSION_KEY, JSON.stringify(session))
  } catch { /* quota */ }
}

export function clearSession(storage: Storage = sessionStorage): void {
  try {
    storage.removeItem(SESSION_KEY)
  } catch { /* ignore */ }
}

export function createEmptySession(): TestSession {
  return {
    id: `sess-${Date.now()}`,
    createdAt: Date.now(),
    memberIds: [],
    activeMemberId: null,
    members: {},
  }
}

export function setActiveMember(session: TestSession, memberId: string): TestSession {
  return { ...session, activeMemberId: memberId }
}

export function updateSessionDraft(
  session: TestSession,
  memberId: string,
  draft: TestSessionDraft,
): TestSession {
  const existing = session.members[memberId] ?? { memberId, completion: 'empty' as MemberCompletionState }
  return {
    ...session,
    members: {
      ...session.members,
      [memberId]: { ...existing, completion: 'draft', draft },
    },
  }
}

export function clearSessionDraft(session: TestSession, memberId: string): TestSession {
  const existing = session.members[memberId]
  if (!existing || existing.completion !== 'draft') return session
  const { draft: _, ...rest } = existing
  return {
    ...session,
    members: {
      ...session.members,
      [memberId]: { ...rest, completion: 'empty' },
    },
  }
}

export function markMemberSaved(session: TestSession, memberId: string): TestSession {
  const existing = session.members[memberId] ?? { memberId, completion: 'empty' as MemberCompletionState }
  const { draft: _, ...rest } = existing
  return {
    ...session,
    members: {
      ...session.members,
      [memberId]: { ...rest, completion: 'saved' },
    },
  }
}

export function addMemberToSession(session: TestSession, memberId: string): TestSession {
  if (session.memberIds.includes(memberId)) return session
  return {
    ...session,
    memberIds: [...session.memberIds, memberId],
    members: {
      ...session.members,
      [memberId]: { memberId, completion: 'empty' },
    },
    activeMemberId: session.activeMemberId ?? memberId,
  }
}

export function removeMemberFromSession(session: TestSession, memberId: string): TestSession {
  const { [memberId]: _removed, ...remainingMembers } = session.members
  const { [memberId]: _removedGuest, ...remainingGuests } = session.guests ?? {}
  const remainingIds = session.memberIds.filter(id => id !== memberId)
  const nextActive = session.activeMemberId === memberId
    ? (remainingIds[0] ?? null)
    : session.activeMemberId
  return {
    ...session,
    memberIds: remainingIds,
    members: remainingMembers,
    guests: remainingGuests,
    activeMemberId: nextActive,
  }
}

export function completionForMember(
  session: TestSession,
  memberId: string,
): MemberCompletionState {
  return session.members[memberId]?.completion ?? 'empty'
}

export function hasDrafts(session: TestSession | null): boolean {
  if (!session) return false
  return session.memberIds.some(id => {
    const state = session.members[id]?.completion
    return state === 'draft'
  })
}

export function savedCount(session: TestSession): number {
  return session.memberIds.filter(id => session.members[id]?.completion === 'saved').length
}

export function draftCount(session: TestSession): number {
  return session.memberIds.filter(id => session.members[id]?.completion === 'draft').length
}

export function getSessionMember(session: TestSession, memberId: string): TestSessionMember | undefined {
  return session.members[memberId]
}

// ---- guest testers (free-form, session-only) ----

export const GUEST_ID_PREFIX = 'guest-'

export function isGuestId(id: string): boolean {
  return id.startsWith(GUEST_ID_PREFIX)
}

/** Next free "Tester N" label: smallest positive N not used by an existing guest. */
export function nextGuestName(session: TestSession): string {
  const used = new Set<number>()
  for (const guest of Object.values(session.guests ?? {})) {
    const match = /^Tester (\d+)$/.exec(guest.name.trim())
    if (match) used.add(Number(match[1]))
  }
  let n = 1
  while (used.has(n)) n++
  return `Tester ${n}`
}

/**
 * Adds a free-form guest tester. When rank and name are both blank the guest
 * is auto-named "Tester N". The new guest always becomes the active tester
 * (a guest add is a "start scoring this person now" action, unlike roster
 * adds which only set active when none is set).
 */
export function addGuestToSession(
  session: TestSession,
  input: GuestTesterInput,
): { session: TestSession; guest: GuestTester } {
  const rank = (input.rank ?? '').trim()
  let name = (input.name ?? '').trim()
  if (!rank && !name) name = nextGuestName(session)
  const guest: GuestTester = {
    id: `${GUEST_ID_PREFIX}${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    rank,
    name,
    jerseyColor: (input.jerseyColor ?? '').trim(),
    jerseyNumber: (input.jerseyNumber ?? '').trim(),
    createdAt: Date.now(),
  }
  return {
    guest,
    session: {
      ...session,
      guests: { ...(session.guests ?? {}), [guest.id]: guest },
      memberIds: [...session.memberIds, guest.id],
      members: {
        ...session.members,
        [guest.id]: { memberId: guest.id, completion: 'empty' },
      },
      activeMemberId: guest.id,
    },
  }
}

/** Marks a guest saved, storing their score on the session entry (guests have no roster history). */
export function markGuestSaved(
  session: TestSession,
  guestId: string,
  entry: UFPMScoreEntry,
): TestSession {
  const existing = session.members[guestId] ?? { memberId: guestId, completion: 'empty' as MemberCompletionState }
  const { draft: _, ...rest } = existing
  return {
    ...session,
    members: {
      ...session.members,
      [guestId]: { ...rest, completion: 'saved', savedScore: entry },
    },
  }
}

export function guestLabel(guest: GuestTester): string {
  return [guest.rank, guest.name].map(s => s.trim()).filter(Boolean).join(' ') || 'Unnamed Tester'
}

export function guestJerseyMeta(guest: GuestTester): string {
  const color = guest.jerseyColor.trim()
  const num = guest.jerseyNumber.trim()
  if (color && num) return `${color} · #${num}`
  if (color) return color
  if (num) return `#${num}`
  return ''
}

export function rosterSavedCount(session: TestSession): number {
  return session.memberIds.filter(
    id => !isGuestId(id) && session.members[id]?.completion === 'saved'
  ).length
}

export function guestSavedCount(session: TestSession): number {
  return session.memberIds.filter(
    id => isGuestId(id) && session.members[id]?.completion === 'saved'
  ).length
}

/**
 * Confirm-dialog message for clearing the session, or null when no
 * confirmation is needed. Roster scores persist in unit history; guest
 * scores and drafts are lost with the session — the wording must never
 * claim guest scores are kept.
 */
export function clearSessionConfirmMessage(session: TestSession): string | null {
  const rs = rosterSavedCount(session)
  const gs = guestSavedCount(session)
  const dc = draftCount(session)
  if (rs === 0 && gs === 0 && dc === 0) return null
  const parts: string[] = []
  if (rs > 0) parts.push(`${rs} saved score${rs !== 1 ? 's' : ''} will be kept in unit history`)
  if (gs > 0) parts.push(`${gs} guest score${gs !== 1 ? 's' : ''} will be discarded`)
  if (dc > 0) parts.push(`${dc} unsaved draft${dc !== 1 ? 's' : ''} will be discarded`)
  return `Clear test session? ${parts.join(', ')}.`
}
